import type {
  DraftResponse,
  DraftRow,
  BillingTarget,
  ContributingEvent,
  DayHours,
  DraftNote,
  RowConfidence,
  AttributionSource,
  WrittenState,
} from '../api/draft';

import type { SourceId } from '../api/calendar';
import type { TriageDraft } from '../stores/triage';

// Applies the user's edit overlay (A.12 triage store) on top of the engine
// draft so the triage panel renders what the user has actually decided so far:
// a picked needs-input event leaves the queue and lands in the grid under the
// chosen target; a re-targeted cell moves its hours from the old target to the
// new one. The engine draft itself is never mutated — this is a view-only
// projection recomputed whenever the overlay changes.

function targetKey(t: { wbs1: string; wbs2?: string; wbs3?: string; category?: string }): string {
  return `${t.wbs1}|${t.wbs2 ?? ''}|${t.wbs3 ?? ''}|${t.category ?? ''}`;
}

interface Contribution {
  eventId: string;
  dayIndex: number;
  hours: number;
  signatureHash: string;
  // Recorded by the engine at draft time. Absent on overlay picks, which the
  // live source read covers.
  source?: SourceId;
}

interface Bucket {
  target: BillingTarget;
  confidence: RowConfidence;
  reasoning: string;
  attributionSource?: AttributionSource;
  // Carried from the engine row; undefined on user-confirmed overlay entries
  // (the pickers gate to a leaf, so overlay rows are always complete).
  isPhased?: boolean;
  // True once a confirmed overlay pick lands in this bucket — drives the grid's
  // 'confirmed' confidence rail (blue) over the engine's target confidence.
  confirmed?: boolean;
  contributions: Contribution[];
}

// A confirmed pick re-attributes an event, a rejection drops it from view, an
// unassign returns it to the needs-input queue. All three remove the event from
// wherever the engine placed it.
function overridesView(edit: TriageDraft | undefined): boolean {
  return !!edit && (edit.status === 'confirmed' || edit.status === 'rejected' || edit.status === 'unassigned');
}

// An unassigned event is waiting on a fresh pick, so it belongs in the queue —
// unlike a confirm (now a grid row) or a dismiss (gone from view).
function leavesQueue(edit: TriageDraft | undefined): boolean {
  return !!edit && (edit.status === 'confirmed' || edit.status === 'rejected');
}

export function reconcileDraft(draft: DraftResponse, overlay: TriageDraft[]): DraftResponse {
  const edits = new Map<string, TriageDraft>();
  for (const e of overlay) edits.set(e.eventId, e);

  const buckets = new Map<string, Bucket>();
  // Preserve the engine's per-(target, day) note so the cell-detail view can
  // show it; cells that move to a new target fall back to an empty note.
  const notesByCell = new Map<string, DraftNote>();
  const emptyNote: DraftNote = { text: '', kind: 'agent', confidence: 'high' };
  const bucketFor = (
    target: BillingTarget,
    confidence: RowConfidence,
    reasoning: string,
    attributionSource?: AttributionSource,
    isPhased?: boolean,
  ): Bucket => {
    const key = targetKey(target);
    let b = buckets.get(key);
    if (!b) {
      b = { target, confidence, reasoning, attributionSource, isPhased, contributions: [] };
      buckets.set(key, b);
    }
    return b;
  };

  // Already-written work, keyed by the event that produced it. The server marks
  // both shapes — a row's day cell, and a held card by its event id — and a
  // card the user attributes turns into a row below, so the mark has to travel
  // with the event rather than with the shape it arrived in (#6487).
  const writtenByEvent = new Map<string, WrittenState>();
  // A manual entry has no row on the response to carry a mark, so the server
  // sends its own marks keyed by event id (#6544). Without them, work added by
  // hand is offered again on every load after the save that wrote it.
  for (const [eventId, w] of Object.entries(draft.writtenEvents ?? {})) {
    writtenByEvent.set(eventId, w);
  }
  for (const row of draft.rows) {
    const byDay = new Map(row.days.map((d) => [d.dayIndex, d.written]));
    for (const ce of row.contributingEvents) {
      const w = byDay.get(ce.dayIndex);
      if (w) writtenByEvent.set(ce.eventId, w);
    }
  }
  for (const ni of draft.needsInput) {
    if (ni.written) writtenByEvent.set(ni.eventId, ni.written);
  }
  // The user overrules a written mark, whichever kind it is (#6487). in-VP is a
  // shape-match guess on project, day and hours. Published is this app's own
  // record of a save, and VP may no longer hold it — deleted, or a period reset
  // — in which case the record hides real hours. The user can see the sheet.
  const overridden = new Set(overlay.filter((e) => e.useAnyway).map((e) => e.eventId));
  for (const id of overridden) writtenByEvent.delete(id);
  const writtenFor = (
    contributions: { eventId: string; dayIndex: number }[],
    dayIndex: number,
  ): WrittenState | undefined => {
    for (const c of contributions) {
      if (c.dayIndex !== dayIndex) continue;
      const w = writtenByEvent.get(c.eventId);
      if (w) return w;
    }
    return undefined;
  };

  // 1. Engine rows → contributions, dropping events the overlay has moved/removed.
  for (const row of draft.rows) {
    const b = bucketFor(row.target, row.confidence, row.reasoning, row.attributionSource, row.isPhased);
    for (const ce of row.contributingEvents) {
      if (overridesView(edits.get(ce.eventId))) continue;
      b.contributions.push({
        eventId: ce.eventId,
        dayIndex: ce.dayIndex,
        hours: ce.hours,
        signatureHash: ce.signatureHash,
        ...(ce.source ? { source: ce.source } : {}),
      });
    }
    for (const day of row.days) {
      notesByCell.set(`${targetKey(row.target)}|${day.dayIndex}`, day.note);
    }
  }

  // 2. Confirmed picks → contributions at the chosen target (user-certain).
  for (const edit of overlay) {
    if (edit.status !== 'confirmed' || !edit.wbs1 || !(edit.hours && edit.hours > 0)) continue;
    const target: BillingTarget = {
      wbs1: edit.wbs1,
      wbs1Name: edit.wbs1Name ?? edit.wbs1,
      wbs2: edit.wbs2 ?? '',
      wbs2Name: edit.wbs2Name ?? '',
      wbs3: edit.wbs3 ?? '',
      wbs3Name: edit.wbs3Name ?? '',
      ...(edit.category ? { category: edit.category } : {}),
    };
    const existing = buckets.get(targetKey(target));
    // A confirmed pick to a phased target always carries a wbs2 (the wizard /
    // inline picker gate to a leaf before committing), so wbs2 presence flags
    // the row phased — this makes a wizard-created row's phase/task cells
    // editable like any drafted row rather than inert.
    const b = existing ?? bucketFor(
      target,
      { target: 'high', hours: 'high', billable: 'low' },
      'You picked this project.',
      'manual',
      !!target.wbs2,
    );
    b.confirmed = true;
    b.contributions.push({
      eventId: edit.eventId,
      dayIndex: edit.dayIndex ?? 0,
      hours: edit.hours,
      signatureHash: edit.signatureHash ?? '',
    });
    // A user-edited/regenerated note rides on the overlay comment — it wins
    // over the engine note for that cell, including an explicit clear
    // (comment === '' suppresses the engine note; absent comment leaves it).
    // commentKind keeps a carried agent note attributed to the agent.
    if (edit.comment !== undefined) {
      notesByCell.set(
        `${targetKey(target)}|${edit.dayIndex ?? 0}`,
        { text: edit.comment, kind: edit.commentKind ?? 'edited', confidence: 'high' },
      );
    }
  }

  // 3. Buckets → rows (sum hours per day; drop empty rows).
  const rows: DraftRow[] = [];
  for (const b of buckets.values()) {
    // Dedupe by (eventId, dayIndex): an event contributes to a (target, day)
    // exactly once. The engine row and an overlay confirm of the same event can
    // both land in this bucket, and an engine row can carry a duplicate ref —
    // either would otherwise double the cell's hours and emit a duplicate save
    // entry (Bug 5956 follow-up). Distinct events on the same day (different
    // eventId) still both count.
    const seenContribs = new Set<string>();
    b.contributions = b.contributions.filter((c) => {
      const k = `${c.eventId}|${c.dayIndex}`;
      if (seenContribs.has(k)) return false;
      seenContribs.add(k);
      return true;
    });
    const byDay = new Map<number, number>();
    for (const c of b.contributions) byDay.set(c.dayIndex, (byDay.get(c.dayIndex) ?? 0) + c.hours);
    const days: DayHours[] = [...byDay.entries()]
      .filter(([, hours]) => hours > 0)
      .sort((a, c) => a[0] - c[0])
      .map(([dayIndex, hours]) => ({
        dayIndex,
        hours,
        note: notesByCell.get(`${targetKey(b.target)}|${dayIndex}`) ?? emptyNote,
        // Carry the already-written mark through the rebuild. A card the user
        // attributed becomes a row here, and dropping the flag is what made
        // saved work reappear as outstanding (#6487). Any contributing event
        // known to be written marks the cell.
        ...(writtenFor(b.contributions, dayIndex) ? { written: writtenFor(b.contributions, dayIndex)! } : {}),
      }));
    if (days.length === 0) continue;
    const contributingEvents: ContributingEvent[] = b.contributions.map((c) => ({
      eventId: c.eventId,
      hours: c.hours,
      signatureHash: c.signatureHash,
      dayIndex: c.dayIndex,
      ...(c.source ? { source: c.source } : {}),
    }));
    rows.push({
      target: b.target,
      days,
      contributingEvents,
      confidence: b.confidence,
      reasoning: b.reasoning,
      attributionSource: b.attributionSource,
      ...(b.isPhased !== undefined ? { isPhased: b.isPhased } : {}),
      ...(b.confirmed ? { confirmed: true } : {}),
    });
  }

  // 4. Needs-input → drop events resolved or dismissed by the overlay, and
  // re-queue the ones the user unassigned. An unassigned event the engine had
  // queued keeps its original card; one the engine had attributed is re-queued
  // from the overlay entry alone, and the drawer joins it back to the live
  // event for its subject and time.
  // An overridden held card returns to the queue: it carries no target, so the
  // mark is the only thing standing between it and the user's attention.
  const needsInput = draft.needsInput
    .filter((ni) => !leavesQueue(edits.get(ni.eventId)))
    .map((ni) => (ni.written && overridden.has(ni.eventId) ? { ...ni, written: undefined } : ni));

  const queued = new Set(needsInput.map((ni) => ni.eventId));
  for (const edit of overlay) {
    if (edit.status !== 'unassigned' || queued.has(edit.eventId)) continue;
    needsInput.push({
      eventId: edit.eventId,
      hours: edit.hours,
      dayIndex: edit.dayIndex,
      signatureHash: edit.signatureHash,
      candidateTargets: [],
      reason: 'You removed this entry.',
    });
  }

  return { ...draft, rows, needsInput };
}

// What VP says a row's project still needs: a phase (`phased`, by WBS1) or a
// task under the row's phase (`tasked`, by "WBS1|WBS2"). See useVpWbsRequirements.
export interface VpWbsRequirements {
  phased: ReadonlySet<string>;
  tasked: ReadonlySet<string>;
}

// A row reads as complete unless the engine looked its project up, so a row the
// user saved without its phase or task hid from the grid's "Select phase" /
// "Select task" and from both submit gates. VP's answer flags it here.
export function applyVpWbsRequirements(draft: DraftResponse, req: VpWbsRequirements): DraftResponse {
  if (req.phased.size === 0 && req.tasked.size === 0) return draft;
  return {
    ...draft,
    rows: draft.rows.map((row) => {
      const { wbs1, wbs2, wbs3, category } = row.target;
      if (category) return row;
      if (!wbs2 && !row.isPhased && req.phased.has(wbs1)) return { ...row, isPhased: true };
      if (wbs2 && !wbs3 && !row.needsTask && req.tasked.has(`${wbs1}|${wbs2}`)) {
        return { ...row, isPhased: true, needsTask: true };
      }
      return row;
    }),
  };
}

// Rows still missing a level VP requires: a phase, or a task under their phase.
export function rowsNeedingLeaf(draft: DraftResponse | null): { phase: number; task: number } {
  const rows = draft?.rows ?? [];
  return {
    phase: rows.filter((r) => r.isPhased && !r.target.wbs2).length,
    task: rows.filter((r) => r.needsTask && !!r.target.wbs2 && !r.target.wbs3).length,
  };
}

export function leafNeededMessage({ phase, task }: { phase: number; task: number }): string {
  const n = phase + task;
  if (n === 0) return '';
  return `${n} ${n === 1 ? 'entry needs' : 'entries need'} ${task > 0 ? 'a phase or task' : 'a phase'} before you can submit.`;
}
